import { Domain } from '@3nickels/data-modules';
import { DefaultPricebook } from '../../../models/PricebookData';
import { t } from 'i18next';
import { formatDecimalDollars, formatWholeDollars } from '../../../helpers/utilityFunctions';

export const resolveHeaders = (products: Domain.PricebookBundle[]) => {
  const headers = [];
  if (
    products.some((p) => p.prices[0].price === 0 && p.pricebook.paidBy === Domain.PaidByEnum.USER)
  ) {
    headers.push('Basic Tools');
  }
  if (
    products.some(
      (p) =>
        p.pricebook.productType === Domain.PricebookProductTypeEnum.NICKELS && p.prices[0].price > 0
    )
  ) {
    headers.push('Advice');
  }
  if (products.some((p) => p.pricebook.productType === Domain.PricebookProductTypeEnum.NICKELSU)) {
    headers.push('Advice + 3NickelsU');
  }
  return headers;
};

export function getCurrentPlan(
  subscription: Domain.SubscriptionInfo | undefined,
  inAdviceFreeTrial: boolean,
  threeNickelsProducts: Domain.PricebookBundle[] | undefined
): Domain.PricebookBundle | undefined {
  const status = subscription?.mappedStatus;

  const advicePricebook =
    threeNickelsProducts?.find(
      (p) =>
        p.pricebook.productType === Domain.PricebookProductTypeEnum.NICKELS && p.prices[0].price > 0
    ) ?? DefaultPricebook;

  const freePricebook =
    threeNickelsProducts?.find(
      (p) =>
        p.pricebook.productType === Domain.PricebookProductTypeEnum.NICKELS &&
        p.prices[0].price === 0 &&
        p.pricebook.paidBy === Domain.PaidByEnum.USER
    ) ?? DefaultPricebook;

  if (subscription && !status?.expired) {
    if (subscription.origin === Domain.PaymentOriginEnum.STRIPE) {
      const pricebook = threeNickelsProducts?.find(
        (p) => p.pricebook.id === subscription.pricebookId
      );
      return pricebook ?? DefaultPricebook;
    } else {
      return advicePricebook;
    }
  } else if (inAdviceFreeTrial) {
    return undefined; // Return undefined so user can see action button to subscribe
  } else {
    return freePricebook;
  }
}

export const getName = (
  subscription: Domain.SubscriptionInfo,
  threeNickelsProducts: Domain.PricebookBundle[] | undefined
): string => {
  if (subscription.origin === Domain.PaymentOriginEnum.STRIPE) {
    const pricebook = threeNickelsProducts?.find(
      (p) => p.pricebook.id === subscription.pricebookId
    );
    return pricebook?.pricebook.pricebookName ?? '';
  } else {
    return subscription.name;
  }
};

export const getInterval = (interval: Domain.PaymentIntervalEnum | undefined): string => {
  if (interval) {
    if (interval !== Domain.PaymentIntervalEnum.NONE) {
      return t(interval.toLocaleLowerCase());
    }
    return t('month');
  }
  return t('month');
};

export const formatPrice = (price: number | undefined) => {
  if (typeof price === 'undefined' || price === null) return;
  const formattedPrice = price / 100;
  const useDecimalFormat = formattedPrice.toString().includes('.');
  return useDecimalFormat
    ? formatDecimalDollars(formattedPrice)
    : formatWholeDollars(formattedPrice);
};

export function getRecentPlan(
  subscription: Domain.SubscriptionInfo | undefined,
  inAdviceFreeTrial: boolean,
  threeNickelsProducts: Domain.PricebookBundle[] | undefined
): Domain.PricebookBundle {
  const advicePricebook =
    threeNickelsProducts?.find(
      (p) =>
        p.pricebook.productType === Domain.PricebookProductTypeEnum.NICKELS && p.prices[0].price > 0
    ) ?? DefaultPricebook;
  const freePricebook =
    threeNickelsProducts?.find(
      (p) =>
        p.pricebook.productType === Domain.PricebookProductTypeEnum.NICKELS &&
        p.prices[0].price === 0 &&
        p.pricebook.paidBy === Domain.PaidByEnum.USER
    ) ?? DefaultPricebook;
  if (subscription) {
    if (subscription.origin === Domain.PaymentOriginEnum.STRIPE) {
      // users who paid through apple/google do not have a pricebook id associated with their subscription
      // subscription.pricebookId is only defined if user paid through stripe
      const pricebook = threeNickelsProducts?.find(
        (p) => p.pricebook.id === subscription.pricebookId
      );
      return pricebook ?? DefaultPricebook;
    } else {
      // users who paid through apple/google are under the retail group and only have advice as an available pricebook
      return advicePricebook;
    }
  } else if (inAdviceFreeTrial) {
    return advicePricebook;
  } else if (threeNickelsProducts && threeNickelsProducts.length === 1) {
    return threeNickelsProducts[0];
  } else {
    return freePricebook;
  }
}
